/**
 * Hand Hygiene Sheet Validation Utilities
 * Contains all validation logic for hand hygiene sheets
 */

/**
 * Helper function to validate required fields
 * @param {Object} dataToValidate - The sheet data to validate
 * @param {Array} errors - Array to collect validation errors
 * @param {Function} t - Translation function
 * @param {Object} vocabs - Vocabulary data for validation
 */
const validateRequiredFields = (dataToValidate, errors, t, vocabs = null) => {
    const requiredFields = [
        { field: 'DepartmentType', errorKey: 'departmentTypeRequired' },
        { field: 'StartObservation', errorKey: 'startDateTimeRequired' },
        { field: 'EndObservation', errorKey: 'endDateTimeRequired' },
        { field: 'Department', errorKey: 'departmentRequired' },
        { field: 'FunctionalArea', errorKey: 'functionalAreaRequired' }
    ];

    // Add HealthAuthority validation only if authorities are available in vocabs
    if (vocabs && vocabs.authorities && vocabs.authorities.length > 0) {
        requiredFields.push({ field: 'HealthAuthority', errorKey: 'authorityRequired' });
    }

    // Helper function to check if a field is truly empty
    const isEmpty = (value) => {
        return value === null || 
               value === undefined || 
               (typeof value === 'string' && value.trim() === '') ||
               (Array.isArray(value) && value.length === 0);
    };

    // Helper function to check if a value exists in vocabulary
    const isValidInVocabulary = (field, value, vocabs) => {
        if (!vocabs || isEmpty(value)) return false;
        
        switch (field) {
            case 'HealthAuthority':
                return vocabs.authorities?.some(auth => auth.Id === value) || false;
            case 'Department':
                return vocabs.departments?.some(dept => dept.Id === value) || false;
            case 'FunctionalArea':
                return vocabs.filteredAreas?.some(area => area.Id === value) || false;
            case 'DepartmentType':
                return vocabs.departmentCategories?.some(cat => cat.Code === value) || false;
            default:
                return true; // For fields without vocabulary validation
        }
    };

    requiredFields.forEach(({ field, errorKey }) => {
        const fieldValue = dataToValidate[field];
        const fieldIsEmpty = isEmpty(fieldValue);
        
        // For vocabulary-based fields, also check if the value exists in the vocabulary
        const needsVocabValidation = ['HealthAuthority', 'Department', 'FunctionalArea', 'DepartmentType'].includes(field);
        const isValidValue = needsVocabValidation ? isValidInVocabulary(field, fieldValue, vocabs) : !fieldIsEmpty;
        

        
        if (fieldIsEmpty || (needsVocabValidation && !isValidValue)) {
            errors.push(t(errorKey, { ns: 'labels' }));
        }
    });
};

/**
 * Helper function to validate date range
 * @param {Object} dataToValidate - The sheet data to validate
 * @param {Array} errors - Array to collect validation errors
 * @param {Function} t - Translation function
 */
const validateDateRange = (dataToValidate, errors, t) => {
    if (dataToValidate.StartObservation && dataToValidate.EndObservation) {
        const start = new Date(dataToValidate.StartObservation);
        const end = new Date(dataToValidate.EndObservation);

        if (end < start) {
            errors.push(t('endDateTimeBeforeStart', { ns: 'labels' }));
        }
    }
};

/**
 * Helper function to validate opportunity completeness
 * @param {Object} opportunity - The opportunity object to validate
 * @param {number} operatorOrder - The operator order number
 * @param {number} opportunityIndex - The opportunity index
 * @param {Array} errors - Array to collect validation errors
 * @param {Function} t - Translation function
 */
const validateOpportunityCompleteness = (opportunity, operatorOrder, opportunityIndex, errors, t) => {
    const hasSelectedAzioni = opportunity.selectedAzioni?.length > 0;
    const hasSelectedOpportunita = opportunity.selectedOpportunita?.length > 0;

    // Check if one is selected but not the other (incomplete opportunity)
    if ((hasSelectedOpportunita && !hasSelectedAzioni) || 
        (hasSelectedAzioni && !hasSelectedOpportunita)) {
        errors.push(t('incompleteOpportunity', {
            ns: 'labels',
            oppIndex: opportunityIndex + 1,
            operatorOrder
        }));
    }
};

/**
 * Helper function to validate opportunity configuration fields
 * @param {Object} opportunity - The opportunity object to validate
 * @param {number} operatorOrder - The operator order number
 * @param {number} opportunityIndex - The opportunity index
 * @param {Array} errors - Array to collect validation errors
 * @param {Object} config - Configuration object
 * @param {Function} t - Translation function
 */
const validateOpportunityConfig = (opportunity, operatorOrder, opportunityIndex, errors, config, t) => {
    if (config?.ShowType && !opportunity.Type) {
        errors.push(t('missingOpportunityType', {
            ns: 'labels',
            oppIndex: opportunityIndex + 1,
            operatorOrder
        }));
    }

    if (config?.ShowGender && !opportunity.Gender) {
        errors.push(t('missingGenderType', {
            ns: 'labels',
            oppIndex: opportunityIndex + 1,
            operatorOrder
        }));
    }
};

/**
 * Helper function to validate that opportunities exist when main data is filled
 * @param {Object} dataToValidate - The sheet data to validate
 * @param {Array} errors - Array to collect validation errors
 * @param {Function} t - Translation function
 */
const validateOpportunitiesExist = (dataToValidate, errors, t) => {
    // Check if main data fields are filled
    const hasMainData = dataToValidate.DepartmentType || 
                       dataToValidate.Department || 
                       dataToValidate.FunctionalArea || 
                       dataToValidate.StartObservation || 
                       dataToValidate.EndObservation;

    if (!hasMainData) return; // If no main data, skip this validation

    // Check if there are any valid opportunities
    const hasValidOpportunities = dataToValidate.ObservedOperators && 
                                 dataToValidate.ObservedOperators.length > 0 &&
                                 dataToValidate.ObservedOperators.some(operator => {
                                     return operator.Opportunities && 
                                            operator.Opportunities.length > 0 &&
                                            operator.Opportunities.some(opportunity => {
                                                const hasSelectedAzioni = Array.isArray(opportunity.selectedAzioni) && 
                                                                         opportunity.selectedAzioni.length > 0;
                                                const hasSelectedOpportunita = Array.isArray(opportunity.selectedOpportunita) && 
                                                                              opportunity.selectedOpportunita.length > 0;
                                                return hasSelectedAzioni || hasSelectedOpportunita;
                                            });
                                 });

    if (!hasValidOpportunities) {
        errors.push(t('noOpportunitiesWithMainData', { ns: 'labels' }));
    }
};

/**
 * Helper function to validate observed operators
 * @param {Object} dataToValidate - The sheet data to validate
 * @param {Array} errors - Array to collect validation errors
 * @param {Object} config - Configuration object
 * @param {Function} t - Translation function
 */
const validateObservedOperators = (dataToValidate, errors, config, t) => {
    if (!dataToValidate.ObservedOperators) return;

    dataToValidate.ObservedOperators.forEach(observedOperator => {
        // Check if operator has opportunities but no category
        const hasOpportunities = observedOperator.Opportunities?.length > 0;
        if (hasOpportunities && !observedOperator.CategoryOperator) {
            errors.push(t('opportunityMissingOperator', {
                ns: 'labels',
                opp: observedOperator.Observation_items_order
            }));
        }

        // Validate each opportunity
        observedOperator.Opportunities?.forEach((opportunity, index) => {
            validateOpportunityCompleteness(
                opportunity, 
                observedOperator.Observation_items_order, 
                index, 
                errors,
                t
            );
            
            validateOpportunityConfig(
                opportunity, 
                observedOperator.Observation_items_order, 
                index, 
                errors,
                config,
                t
            );
        });
    });
};

/**
 * Main validation function for hand hygiene sheets
 * @param {Object} dataToValidate - The sheet data to validate
 * @param {Object} config - Configuration object
 * @param {Function} t - Translation function
 * @param {Object} vocabs - Vocabulary data for validation
 * @returns {Promise<Array>} Array of validation errors
 */
export const validateHandHygieneSheet = async (dataToValidate, config, t, vocabs = null) => {
    const errors = [];

    // Validate required fields
    validateRequiredFields(dataToValidate, errors, t, vocabs);

    // Validate date range
    validateDateRange(dataToValidate, errors, t);

    // Validate that opportunities exist when main data is filled
    validateOpportunitiesExist(dataToValidate, errors, t);

    // Validate observed operators and their opportunities
    validateObservedOperators(dataToValidate, errors, config, t);

    return errors;
};

/**
 * Converts an opportunity object into a format suitable for the backend (database table)
 * @param {Object} opportunity - The opportunity object to convert
 * @param {Object} observation - The observation object
 * @returns {Object} Converted opportunity object for backend
 */
export const convertOpportunitiesFromObjectToDbTable = (opportunity, observation) => {
    const newOpportunity = {
        Id: opportunity.Id,
        OrderItem: opportunity.OrderItem,
        ObservationId: observation.Id,
        Type: opportunity.Type,
        Gender: opportunity.Gender,
    }

    if (opportunity.selectedAzioni) {
        opportunity.selectedAzioni.forEach(sa => {
            if (sa == "missed") {
                newOpportunity.ActionMissed = true;
            }
            if (sa == "hr") {
                newOpportunity.ActionHR = true;
            }
            if (sa == "hw") {
                newOpportunity.ActionHW = true;
            }
            if (sa == "gloving") {
                newOpportunity.ActionGloving = true;
            }
        });
    }

    if (opportunity.selectedOpportunita) {
        opportunity.selectedOpportunita.forEach(so => {
            if (so == "bef-pat") {
                newOpportunity.IndicationBefPat = true;
            }
            if (so == "bef.asepts") {
                newOpportunity.IndicationBefAsepts = true;
            }
            if (so == "aft-b-f") {
                newOpportunity.IndicationAftBF = true;
            }
            if (so == "aft-pat") {
                newOpportunity.IndicationAftPat = true;
            }
            if (so == "aft-p-surr") {
                newOpportunity.IndicationAftPSurr = true;
            }
        });
    }

    return newOpportunity;
} 